// Fires `page_view` on every route change. Mounted inside the rendered app
// shell (`Agency`) so it's always under `<BrowserRouter>` — `useLocation`
// would throw at the App-level otherwise.
//
// It lived in `TenantLayout` until 2026-08-24, which was never mounted in the
// route tree, so this event silently never fired. Mount it in the component
// that actually wraps `privateRoutes`, and nowhere else.
//
// Query string is passed through a redaction pass: any param key matching
// the blocklist regex (token, email, phone, ssn, dob, zip, password) is
// replaced with `[redacted]`. The pathname itself is left intact — by
// convention, tenant-admin routes don't embed PII (only IDs / slugs).
//
// SEE: docs/tenant-admin-vexo-analytics-plan.md §3 + §9 edge case #14, #22.

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { track } from './events';

const BLOCKED_PARAM_KEY = /(token|email|phone|ssn|dob|zip|password)/i;

function redactQueryString(search: string): string {
  if (!search) return '';
  const params = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search,
  );
  let mutated = false;
  for (const k of Array.from(params.keys())) {
    if (BLOCKED_PARAM_KEY.test(k)) {
      params.set(k, '[redacted]');
      mutated = true;
    }
  }
  if (!mutated) return search;
  const out = params.toString();
  return out ? `?${out}` : '';
}

export function usePageViewTracking(): void {
  const { pathname, search } = useLocation();

  // Edge case #14: hash-only changes don't fire because `hash` isn't in
  // the deps. Edge case #9: if SDK isn't loaded yet, track() no-ops; the
  // first route is silently lost (acceptable; one event per session).
  useEffect(() => {
    track('page_view', {
      path: pathname,
      search: redactQueryString(search),
    });
  }, [pathname, search]);
}
